import {AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, FormGroup} from '@angular/forms';

/**
 * Represents an API validation error response following RFC 9110 format.
 * Example:
 * {
 *   "type": "https://tools.ietf.org/html/rfc9110#section-15.5.1",
 *   "title": "One or more validation errors occurred.",
 *   "status": 400,
 *   "errors": {
 *     "$.medicalAid.schemeId": ["The JSON value could not be converted to System.Int32..."]
 *   },
 *   "traceId": "..."
 * }
 */
export interface ApiValidationErrorResponse {
  type?: string;
  title?: string;
  status?: number;
  errors?: Record<string, string[]>;
  traceId?: string;
}

export class CustomValidators {

  constructor() {
  }

  static printAllFormErrors(formGroup: UntypedFormGroup | UntypedFormArray, path: string = ''): any[] {
    const errors = [];
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.controls[key] as UntypedFormControl | UntypedFormGroup | UntypedFormArray;
      const name = this.getControlName(control);
      const keypath = path === '' ? name : `${path}.${name}`;
      if (control instanceof UntypedFormControl) {
        if (control.errors !== null) {
          errors.push({ key: keypath, control: control });
        }
      } else if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {        // {5}
        errors.push(...this.printAllFormErrors(control, keypath))  ;      // {6}
      } else {
        console.error('invalid type', control, key, typeof control);
      }
    });
    return errors;
  }

  static validateAllFormFields(formGroup: UntypedFormGroup | UntypedFormArray) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.controls[key] as UntypedFormControl | UntypedFormGroup | UntypedFormArray;
      if (control instanceof UntypedFormControl) {   // {4}
        control.markAsDirty();          // {4}
        control.markAsTouched();
      } else if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {        // {5}
        this.validateAllFormFields(control);            // {6}
      } else {
        console.error('invalid type', control, key, typeof control);
      }
    });
  }

  static atLeastOneValueRequired(...args: { formArrayName: string, formControlName: string }[]): ValidatorFn {
    return (group: UntypedFormGroup): ValidationErrors => {
      const controls: UntypedFormControl[] = [];
      let validControls = false;
      args.forEach(field => {
        const controlArray = group.get(field.formArrayName) as UntypedFormArray;
        if (controlArray) {
          controlArray.controls.forEach((innerGroup: UntypedFormGroup) => {
            const control = innerGroup.controls[field.formControlName] as UntypedFormControl;
            if (control) {
              if (control.value && control.value.length > 0) { validControls = true; }
              controls.push(control);
            }
          });
        }
      });
      if (validControls) {
        controls.forEach(control => {
          if (control.errors && control.errors['atLeastOnValueRequired']) {
            delete control.errors['atLeastOnValueRequired'];
            if (Object.keys(control.errors).length === 0) {
              control.setErrors(null);
            }
            control.updateValueAndValidity();
          }
        });
        return null;
      }
      controls.forEach(control => control.setErrors({ atLeastOnValueRequired: true}));
      return null;
    };
  }

  static conditionallyRequired(): ValidatorFn {
    return (control: UntypedFormControl): ValidationErrors => {
      if (control && (control.dirty || control.touched) && control.value.length === 0) {
        return { required: true };
      }
      return;
    };
  }

  static duplicateFieldMatchRequired(fieldName: string, matchFieldName: string): ValidatorFn {
    return (group: UntypedFormGroup): ValidationErrors => {
      const control = group.get(fieldName) as UntypedFormControl;
      const matchedControl = group.get(matchFieldName) as UntypedFormControl;
      if (control === null || matchedControl === null) {
        return null;
      }

      const primaryEmpty = !control.value || control.value.length === 0;
      const matchEmpty = !matchedControl.value || matchedControl.value.length === 0;

      // If both fields are empty, clear any errors and return valid
      // This handles the case where user clears both fields
      if (primaryEmpty && matchEmpty) {
        if (matchedControl.hasError('notMatching')) {
          const errors = { ...matchedControl.errors };
          delete errors['notMatching'];
          matchedControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
        }
        return null;
      }

      // If primary is empty but match has value, set error (they don't match)
      if (primaryEmpty && !matchEmpty) {
        const existingErrors = matchedControl.errors || {};
        matchedControl.setErrors({ ...existingErrors, notMatching: true });
        return null;
      }

      // If primary is empty and match is empty, already handled above
      // This case: primaryEmpty && matchEmpty - shouldn't reach here but handle defensively
      if (primaryEmpty) {
        if (matchedControl.hasError('notMatching')) {
          const errors = { ...matchedControl.errors };
          delete errors['notMatching'];
          matchedControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
        }
        return null;
      }

      // If primary has value but match is empty, set error
      // (user needs to confirm the value)
      if (matchEmpty) {
        const existingErrors = matchedControl.errors || {};
        matchedControl.setErrors({ ...existingErrors, notMatching: true });
        return null;
      }

      // If values don't match, set error on match field
      if (control.value !== matchedControl.value) {
        const existingErrors = matchedControl.errors || {};
        matchedControl.setErrors({ ...existingErrors, notMatching: true });
        return null;
      }

      // If values match, clear the notMatching error
      if (matchedControl.hasError('notMatching')) {
        const errors = { ...matchedControl.errors };
        delete errors['notMatching'];
        matchedControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
      }

      return null;
    };
  }

  static practiceNumberValidator(): ValidatorFn {
    return (control: UntypedFormControl): ValidationErrors => {
      const value = (control && control.value) ? control.value.replace(/\D/g, '') : '';
      if (value.length !== 13) {
        return {invalid: true};
      }
      return;
    };
  }

  static identityNumberValidator(performLuhnCheck: boolean = false): ValidatorFn {
    return (control: UntypedFormControl): ValidationErrors => {
      const value = (control && control.value) ? control.value.replace(/\D/g, '') : '';
      // valid if it is empty
      if (value.length === 0) {
        return;
      }
      // if it is not 13 digits long there is a problem
      if (value.length !== 13) {
        return {invalid: true};
      }
      // check parameter and if needed, do checksum validation with the Luhn algorithm
      if (performLuhnCheck && !this.luhnCheck(value)) {
        return {invalid: true};
      }
      return;
    };
  }

  private static luhnCheck(num: number | string): boolean {
    let value = num.toString();
    if (/[^0-9-\s]+/.test(value)) {
      return false;
    }
    // The Luhn Algorithm. It's so pretty.
    let nCheck = 0;
    let bEven = false;
    value = value.replace(/\D/g, '');
    for (let n = value.length - 1; n >= 0; n--) {
      const cDigit = value.charAt(n);
      let nDigit = parseInt(cDigit, 10);
      if (bEven && (nDigit *= 2) > 9) {
        nDigit -= 9;
      }
      nCheck += nDigit;
      bEven = !bEven;
    }
    return (nCheck % 10) === 0;
  }

  private static getControlName(c: AbstractControl): string | null {
    const formGroup = c.parent.controls;
    return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
  }

  /**
   * Applies API validation errors to form controls.
   * Parses the RFC 9110 error response format and sets errors on matching form controls.
   *
   * @param form - The form group to apply errors to
   * @param apiError - The API error response (can be the full HttpErrorResponse.error or just the error body)
   * @returns An object with applied errors and any unmapped errors for display
   *
   * @example
   * // In your component's error handler:
   * this.repository.create(data).subscribe({
   *   error: (httpError) => {
   *     const result = CustomValidators.applyApiErrorsToForm(this.form, httpError.error);
   *     if (result.unmappedErrors.length > 0) {
   *       this.generalErrors = result.unmappedErrors;
   *     }
   *   }
   * });
   */
  static applyApiErrorsToForm(
    form: UntypedFormGroup | FormGroup,
    apiError: ApiValidationErrorResponse | any
  ): { applied: string[]; unmappedErrors: { path: string; messages: string[] }[] } {
    const result = {
      applied: [] as string[],
      unmappedErrors: [] as { path: string; messages: string[] }[]
    };

    if (!form) {
      return result;
    }

    // Handle different error response shapes
    const errors = apiError?.errors || apiError?.error?.errors;
    if (!errors || typeof errors !== 'object') {
      return result;
    }

    for (const [path, messages] of Object.entries(errors)) {
      if (!Array.isArray(messages)) {
        continue;
      }

      // Convert API path format to form control path
      // $.medicalAid.schemeId → medicalAid.schemeId
      // Also handles array notation: $.items[0].field → items.0.field
      const controlPath = path
        .replace(/^\$\./, '')           // Remove leading $.
        .replace(/\[(\d+)\]/g, '.$1');  // Convert [0] to .0

      const control = form.get(controlPath);
      if (control) {
        // Create a user-friendly error message
        const friendlyMessage = this.createFriendlyErrorMessage(messages[0], controlPath);
        // Merge with existing errors instead of replacing them
        const existingErrors = control.errors || {};
        control.setErrors({ ...existingErrors, apiError: friendlyMessage });
        control.markAsTouched();
        control.markAsDirty();
        result.applied.push(controlPath);
      } else {
        // Track unmapped errors for display in a general error area
        result.unmappedErrors.push({
          path: controlPath,
          messages: messages.map(m => this.createFriendlyErrorMessage(m, controlPath))
        });
      }
    }

    return result;
  }

  /**
   * Checks if an HTTP error response contains API validation errors.
   *
   * @param error - The error object (typically HttpErrorResponse.error)
   * @returns true if the error contains validation errors
   */
  static isApiValidationError(error: any): error is ApiValidationErrorResponse {
    return error?.errors && typeof error.errors === 'object' && error.status === 400;
  }

  /**
   * Extracts all error messages from an API validation error response.
   *
   * @param apiError - The API error response
   * @returns Array of all error messages
   */
  static getAllApiErrorMessages(apiError: ApiValidationErrorResponse | any): string[] {
    const errors = apiError?.errors || apiError?.error?.errors;
    if (!errors || typeof errors !== 'object') {
      return [];
    }

    const messages: string[] = [];
    for (const [path, pathMessages] of Object.entries(errors)) {
      if (Array.isArray(pathMessages)) {
        const controlPath = path.replace(/^\$\./, '').replace(/\[(\d+)\]/g, '.$1');
        messages.push(...pathMessages.map(m => this.createFriendlyErrorMessage(m, controlPath)));
      }
    }
    return messages;
  }

  /**
   * Creates a user-friendly error message from an API error message.
   * Transforms technical messages into something more readable.
   */
  private static createFriendlyErrorMessage(message: string, fieldPath: string): string {
    // Handle specific field paths with custom messages
    const fieldSpecificMessages: Record<string, string> = {
      'medicalAid.schemeId': 'Medical Aid Scheme is required',
    };

    if (fieldSpecificMessages[fieldPath]) {
      return fieldSpecificMessages[fieldPath];
    }

    // Extract just the field name from the path for display
    const fieldName = fieldPath.split('.').pop() || fieldPath;
    const displayName = this.camelCaseToReadable(fieldName);

    // Handle common API error patterns
    if (message.includes('could not be converted to System.Int32')) {
      return `${displayName} must be a valid number`;
    }
    if (message.includes('could not be converted to System.Decimal')) {
      return `${displayName} must be a valid decimal number`;
    }
    if (message.includes('could not be converted to System.DateTime')) {
      return `${displayName} must be a valid date`;
    }
    if (message.includes('could not be converted to System.Boolean')) {
      return `${displayName} must be true or false`;
    }
    if (message.includes('is required')) {
      return `${displayName} is required`;
    }
    if (message.includes('must be between')) {
      return message; // These are usually already readable
    }

    // Default: return the original message if no pattern matches
    return message;
  }

  /**
   * Converts camelCase to readable text.
   * e.g., 'schemeId' → 'Scheme Id', 'medicalAidNumber' → 'Medical Aid Number'
   */
  private static camelCaseToReadable(str: string): string {
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, char => char.toUpperCase())
      .trim();
  }
}
